import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { partyApi } from '../../services/api.party';
import { useDebounce } from '../../utils/useDebounce';
import PartyModal from './PartyModal';
import config from '../../config/environment';
import './Party.css';

const PartyList = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ totalCount: 0, totalPages: 1, currentPage: 1, pageSize: 20 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [message, setMessage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editParty, setEditParty] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const debouncedQ = useDebounce(q, 400);

  const getFullImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    const baseUrl = config.API_URL.replace(/\/api$/, '');
    return `${baseUrl}${url}`;
  };

  const load = async (page = 1) => {
    setLoading(true);
    setMessage(null);
    try {
      const data = await partyApi.list({ search: debouncedQ, page, pageSize: meta.pageSize || 20 });
      setItems(data.items || []);
      setMeta({
        totalCount: data.totalCount || 0,
        totalPages: data.totalPages || 1,
        currentPage: data.currentPage || 1,
        pageSize: data.pageSize || 20
      });
    } catch (e) {
      setMessage({ type: 'error', text: e.message || t('party.loadingParties') });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ]);

  const onCreate = () => {
    setEditParty(null);
    setShowModal(true);
  };

  const onEdit = (party) => {
    setEditParty(party);
    setShowModal(true);
  };

  const onDelete = (party) => {
    setConfirmDelete(party);
  };

  const confirmDeleteAction = async () => {
    if (!confirmDelete) return;
    try {
      await partyApi.remove(confirmDelete.id);
      setMessage({ type: 'success', text: t('party.partyDeleted') });
      setConfirmDelete(null);
      load(meta.currentPage);
    } catch (e) {
      setMessage({ type: 'error', text: e.message || 'Failed to delete party' });
    }
  };

  const badge = (n) => {
    return (
      <span className={`badge ${n > 0 ? 'badge-green' : 'badge-gray'}`}>
        {n}
      </span>
    );
  };

  const pager = useMemo(() => {
    const pages = [];
    const total = meta.totalPages || 1;
    const cur = meta.currentPage || 1;
    for (let i = 1; i <= total; i++) {
      if (i === 1 || i === total || (i >= cur - 2 && i <= cur + 2)) {
        pages.push(i);
      } else if (i === cur - 3 || i === cur + 3) {
        pages.push('...');
      }
    }
    return pages;
  }, [meta]);

  return (
    <div className="party-container">
      <div className="party-header">
        <h2><i className="fas fa-flag"></i> {t('party.title')}</h2>
        <div className="party-actions">
          <div className="search-box">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder={t('party.search')}
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <button className="btn primary" onClick={onCreate}>
            <i className="fas fa-plus"></i> {t('party.newParty')}
          </button>
        </div>
      </div>

      {message && (
        <div className={`alert ${message.type}`}>
          <i className={`fas fa-${message.type === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
          {message.text}
          <button className="close-alert" onClick={() => setMessage(null)}>×</button>
        </div>
      )}

      <div className="party-table-wrap">
        {loading ? (
          <div className="loading-center">
            <div className="spinner-large" />
            <p>{t('party.loadingParties')}</p>
          </div>
        ) : items.length === 0 ? (
          <div className="no-data">
            <i className="fas fa-inbox"></i>
            <p>{t('party.noPartiesFound')}</p>
          </div>
        ) : (
          <table className="party-table">
            <thead>
              <tr>
                <th>{t('party.symbol')}</th>
                <th>{t('party.name')}</th>
                <th>{t('party.shortName')}</th>
                <th>{t('party.candidates')}</th>
                <th>{t('party.lastUsed')}</th>
                <th>{t('party.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map(p => (
                <tr key={p.id}>
                  <td>
                    <div className="symbol-cell">
                      {p.symbolUrl ? (
                        <img 
                          src={getFullImageUrl(p.symbolUrl)} 
                          alt={p.shortName || p.name} 
                          onError={(e) => {
                            console.error('Image load error:', p.symbolUrl, '→', getFullImageUrl(p.symbolUrl));
                            e.target.style.display = 'none';
                          }} 
                        />
                      ) : (
                        <div className="symbol-placeholder"><i className="fas fa-flag"></i></div>
                      )}
                    </div>
                  </td>
                  <td><strong>{p.name}</strong></td>
                  <td>{p.shortName || '—'}</td>
                  <td>{badge(p.candidatesCount || 0)}</td>
                  <td>{p.lastUsed ? new Date(p.lastUsed).toLocaleString() : '—'}</td>
                  <td>
                    <div className="row-actions">
                      <button className="btn-icon" title={t('party.edit')} onClick={() => onEdit(p)}>
                        <i className="fas fa-edit"></i>
                      </button>
                      <button className="btn-icon danger" title={t('party.delete')} onClick={() => onDelete(p)}>
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {meta.totalPages > 1 && (
        <div className="pagination">
          <button
            className="btn-page"
            disabled={meta.currentPage <= 1}
            onClick={() => load(meta.currentPage - 1)}
          >
            <i className="fas fa-chevron-left"></i> {t('party.prev')}
          </button>

          <div className="page-numbers">
            {pager.map((p, idx) =>
              p === '...' ? (
                <span key={`dots-${idx}`} className="page-ellipsis">...</span>
              ) : (
                <button
                  key={p}
                  className={`btn-page ${p === meta.currentPage ? 'active' : ''}`}
                  onClick={() => load(p)}
                >
                  {p}
                </button>
              )
            )}
          </div>

          <button
            className="btn-page"
            disabled={meta.currentPage >= meta.totalPages}
            onClick={() => load(meta.currentPage + 1)}
          >
            {t('party.next')} <i className="fas fa-chevron-right"></i>
          </button>
        </div>
      )}

      {showModal && (
        <PartyModal
          party={editParty}
          onClose={() => {
            setShowModal(false);
            setEditParty(null);
          }}
          onSaved={() => {
            setShowModal(false);
            setEditParty(null);
            load(meta.currentPage);
            setMessage({ type: 'success', text: editParty ? t('party.partyUpdated') : t('party.partyCreated') });
          }}
        />
      )}

      {confirmDelete && (
        <div className="modal-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3><i className="fas fa-exclamation-triangle"></i> {t('party.confirmDelete')}</h3>
              <button className="close-button" onClick={() => setConfirmDelete(null)}>×</button>
            </div>
            <div className="modal-body">
              <p>{t('party.deleteMessage')} <strong>{confirmDelete.name}</strong>?</p>
              <p className="warning-text">
                <i className="fas fa-info-circle"></i>
                {t('party.deleteWarning')}
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setConfirmDelete(null)}>{t('party.cancel')}</button>
              <button className="btn danger" onClick={confirmDeleteAction}>
                <i className="fas fa-trash"></i> {t('party.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartyList;
